import React, { useState, useEffect, useRef } from 'react';
import { Modal, Table, Form, Button } from 'react-bootstrap';
import axios from 'axios';
import config from '../Common_Files/Config';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';

const ProductListModel = ({ onViewPastPurchase, modalData = false, setTableRows, tableRows, getProductBatchData }) => {
  const [productBatch, setProductBatch] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [originalProductList, setOriginalProductList] = useState([]);
  const [showPastPurchaseModal, setShowPastPurchaseModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const modalDataRef = useRef(null);

  const accessToken = localStorage.getItem('accessToken');

  const handleShowPastPurchaseModal = () => {
    setShowPastPurchaseModal(true);
    setIsInitialLoad(false);
    // Reset search state when modal opens
    setSearchQuery('');
    setSelectedProducts(new Set());
    // Restore original products if available
    if (originalProductList.length > 0) {
      setFilteredProducts(originalProductList);
    }
  };

  const handleClosePastPurchaseModal = () => {
    setShowPastPurchaseModal(false);
    // Reset search state when modal closes
    setSearchQuery('');
    setSelectedProducts(new Set());
    // Restore original products
    if (originalProductList.length > 0) {
      setFilteredProducts(originalProductList);
    }
  };

  // Function to determine if data is product data (not batch data)
  const isProductData = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) return false;
    const firstItem = data[0];
    // Product data has product_desc, batch data typically has reference_no and different structure
    return firstItem &&
      typeof firstItem.product_desc === 'string' &&
      firstItem.product_desc.length > 0 &&
      !firstItem.reference_no && // Batch data has reference_no
      typeof firstItem.code === 'string'; // Product data has code
  };

  // Main effect to handle modalData changes - only for initial product load
  useEffect(() => {
    console.log('modalData changed:', modalData);

    if (modalData && modalData.length > 0) {
      // Only process if this is valid product data
      if (isProductData(modalData)) {
        console.log('Setting initial product data:', modalData);
        setProductBatch(modalData);
        setFilteredProducts(modalData);
        setOriginalProductList(modalData);
        modalDataRef.current = modalData; // Store reference to original data
      } else {
        console.log('Received non-product data, ignoring:', modalData[0]);
      }
    }
  }, [modalData]);

  // Effect to restore products when modal is opened and we have original data
  useEffect(() => {
    if (showPastPurchaseModal && originalProductList.length > 0 && !searchQuery) {
      setFilteredProducts(originalProductList);
    }
  }, [showPastPurchaseModal, originalProductList, searchQuery]);

  useEffect(() => {
    if (onViewPastPurchase) {
      onViewPastPurchase(handleShowPastPurchaseModal);
    }
  }, [onViewPastPurchase]);

  const handleSearch = async (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    if (!query) {
      setFilteredProducts(originalProductList);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("searchValue", query);
      const apiUrl = `${config.api_url}/ws-get-products`;
      const response = await axios.post(apiUrl, formData, {
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      });

      const searchResults = response.data.products || [];
      if (searchResults.length > 0) {
        setFilteredProducts(searchResults);
      } else {
        setFilteredProducts(originalProductList);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      setFilteredProducts(originalProductList);
    }
  };

  const toggleSelection = (id) => {
    const updatedSelection = new Set(selectedProducts);
    if (updatedSelection.has(id)) {
      updatedSelection.delete(id);
    } else {
      updatedSelection.add(id);
    }
    setSelectedProducts(updatedSelection);
  };

  const handleProductBatchClick = (e, productId) => {
    e.preventDefault();
    // Call the batch function
    getProductBatchData(productId);
    // Don't close the current modal - let it stay open
  };

  const handleSubmit = async () => {
    const selectedArray = Array.from(selectedProducts);

    for (const id of selectedArray) {
      const formData = new FormData();
      formData.append("id", id);

      const apiUrl = `${config.api_url}/ws-get-products-by-id`;

      try {
        const response = await axios.post(apiUrl, formData, {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        });

        const productData = response.data.products[0];

        setTableRows(prevRows => {
          if (!Array.isArray(prevRows)) {
            console.error("prevRows is not an array:", prevRows);
            return [];
          }

          const existingRow = prevRows.find(element => element.id === productData.id);
          if (existingRow) {
            Swal.fire({
              title: "Error!",
              text: "Product already exists",
              icon: "error",
              timer: 1500,
              showConfirmButton: false,
            });
            return prevRows;
          }

          const newRow = {
            id: productData.id,
            code: productData.code,
            product_desc: productData.product_desc,
            pack: productData.pack,
            size: productData.size,
            price: productData.price,
            cost: productData.cost,
            sys_quantity: productData.quantity,
            free_stock: response.data.freestock,
            free_stock_cal: response.data.freestock,
            sale_qty: (response.data.sale && response.data.sale.length > 0) ? response.data.sale[0].sale_qty : 0,
            quantity: 0,
            taxrate: productData.rate,
            vat: parseFloat(productData.vat),
            value: 0,
            netp: 0,
            retro: 0,
            promptPayment: 0,
            costToServe: 0,
            offInvoice: 0,
            bedsLeds: 0,
            sugroRetro: 0,
            netprice: 0,
            sales_item_id: 0,
            fstk: (response.data.freestock !== undefined && response.data.freestock !== null) ?
              (parseInt(response.data.freestock || 0) + parseInt(productData.quantity || 0)) :
              parseInt(productData.quantity || 0),
          };

          return [...prevRows, newRow];
        });

      } catch (error) {
        console.error("Error fetching product data:", error);
      }
    }

    setSelectedProducts(new Set());
    handleClosePastPurchaseModal();
  };

  return (
    <div>
      <Modal show={showPastPurchaseModal} onHide={handleClosePastPurchaseModal} size="lg">
        <Modal.Header closeButton>
          Search Product 
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            placeholder="Search (Code/Description/Unit Barcode)"
            value={searchQuery}
            onChange={handleSearch}
            className="mb-3"
          />
          <div className="partialbox">
            <Table striped bordered hover className="dataTable">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Pk</th>
                  <th>Size</th>
                  <th>Unit Barcode</th>
                  <th>Stock</th>
                  <th>F. Stk</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((item, index) => {
                    const isSelected = selectedProducts.has(item.id);
                    // Calculate free stock properly
                    const freeStock = (Number(item.freestock || 0) + Number(item.quantity || 0));

                    return (
                      <tr
                        key={`product-${item.id || index}`}
                        onClick={() => toggleSelection(item.id)}
                        style={{ cursor: 'pointer', backgroundColor: isSelected ? '#e6f7ff' : 'inherit' }}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <Form.Control
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(item.id)}
                            className="form-check-input rounded-1" 
                          />
                        </td>
                        <td>
                          {(item.code === 'DEL001' || item.code?.toUpperCase() === 'DEL001') ? (
                            <span style={{ textDecoration: 'none', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
                              {item.code}
                            </span>
                          ) : (
                            <Link
                              to="#"
                              onClick={(e) => handleProductBatchClick(e, item.id)}
                            >
                              {item.code}
                            </Link>
                          )}
                        </td>
                        <td>{item.product_desc}</td>
                        <td>{item.pack}</td>
                        <td>{item.size}</td>
                        <td>{item.unit_barcode}</td>
                        <td>{item.quantity}</td>
                        <td>{(item.code === 'DEL001' || item.code?.toUpperCase() === 'DEL001') ? 0 : (isNaN(freeStock) ? '0' : Math.trunc(freeStock))}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      {originalProductList.length === 0 ? 'No products available. Please load product data.' : 'Loading products...'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleSubmit} className='popup-innerbtn-common'>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductListModel;